'use client';

import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, CheckCircle2, TrendingUp, TrendingDown, Minus, Search, BookmarkPlus, ArrowRight, Brain, LoaderCircle, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

// ============================================================
// DATA
// ============================================================
interface PredictorForm {
  rank: string;
  category: string;
  state: string;
  quota: string;
  course: string;
  budgetMin: number;
  budgetMax: number;
  collegeType: string;
}

interface CollegeResult {
  id: string;
  college: string;
  state: string;
  type: 'Government' | 'Private' | 'Deemed';
  course: string;
  seats: number;
  annualFees: number;
  openingRank: number;
  closingRank: number;
  probability: 'High' | 'Moderate' | 'Low';
  nirf: number | null;
  city: string;
}

interface LeadForm {
  name: string;
  mobile: string;
  email: string;
}

const COLLEGE_DATABASE: CollegeResult[] = [
  { id: 'aiims-del', college: 'AIIMS New Delhi', state: 'Delhi', type: 'Government', course: 'MBBS', seats: 107, annualFees: 1628, openingRank: 1, closingRank: 50, probability: 'High', nirf: 1, city: 'New Delhi' },
  { id: 'aiims-jod', college: 'AIIMS Jodhpur', state: 'Rajasthan', type: 'Government', course: 'MBBS', seats: 100, annualFees: 1628, openingRank: 51, closingRank: 200, probability: 'High', nirf: 4, city: 'Jodhpur' },
  { id: 'aiims-bho', college: 'AIIMS Bhopal', state: 'Madhya Pradesh', type: 'Government', course: 'MBBS', seats: 100, annualFees: 1628, openingRank: 100, closingRank: 280, probability: 'High', nirf: 6, city: 'Bhopal' },
  { id: 'aiims-ris', college: 'AIIMS Rishikesh', state: 'Uttarakhand', type: 'Government', course: 'MBBS', seats: 100, annualFees: 1628, openingRank: 120, closingRank: 320, probability: 'High', nirf: 7, city: 'Rishikesh' },
  { id: 'aiims-pat', college: 'AIIMS Patna', state: 'Bihar', type: 'Government', course: 'MBBS', seats: 100, annualFees: 1628, openingRank: 200, closingRank: 450, probability: 'High', nirf: 9, city: 'Patna' },
  { id: 'aiims-rpr', college: 'AIIMS Raipur', state: 'Chhattisgarh', type: 'Government', course: 'MBBS', seats: 100, annualFees: 1628, openingRank: 250, closingRank: 520, probability: 'High', nirf: 10, city: 'Raipur' },
  { id: 'mamc-del', college: 'Maulana Azad Medical College', state: 'Delhi', type: 'Government', course: 'MBBS', seats: 250, annualFees: 28900, openingRank: 220, closingRank: 312, probability: 'High', nirf: 3, city: 'New Delhi' },
  { id: 'lhmc-del', college: 'Lady Hardinge Medical College', state: 'Delhi', type: 'Government', course: 'MBBS', seats: 200, annualFees: 31500, openingRank: 280, closingRank: 490, probability: 'High', nirf: 8, city: 'New Delhi' },
  { id: 'vmmc-del', college: 'VMMC & Safdarjung Hospital', state: 'Delhi', type: 'Government', course: 'MBBS', seats: 150, annualFees: 35000, openingRank: 450, closingRank: 680, probability: 'High', nirf: 6, city: 'New Delhi' },
  { id: 'afmc-pun', college: 'Armed Forces Medical College', state: 'Maharashtra', type: 'Government', course: 'MBBS', seats: 150, annualFees: 56500, openingRank: 200, closingRank: 430, probability: 'High', nirf: 7, city: 'Pune' },
  { id: 'cmc-vel', college: 'Christian Medical College Vellore', state: 'Tamil Nadu', type: 'Private', course: 'MBBS', seats: 100, annualFees: 85000, openingRank: 500, closingRank: 1200, probability: 'High', nirf: 2, city: 'Vellore' },
  { id: 'seth-mum', college: 'Seth GS Medical College & KEM Hospital', state: 'Maharashtra', type: 'Government', course: 'MBBS', seats: 200, annualFees: 42300, openingRank: 750, closingRank: 1100, probability: 'High', nirf: 5, city: 'Mumbai' },
  { id: 'kgmu-luc', college: 'King George Medical University', state: 'Uttar Pradesh', type: 'Government', course: 'MBBS', seats: 250, annualFees: 42000, openingRank: 800, closingRank: 1200, probability: 'High', nirf: 16, city: 'Lucknow' },
  { id: 'grant-mum', college: 'Grant Medical College & JJ Hospital', state: 'Maharashtra', type: 'Government', course: 'MBBS', seats: 150, annualFees: 38700, openingRank: 1050, closingRank: 1480, probability: 'High', nirf: 11, city: 'Mumbai' },
  { id: 'gmc-tri', college: 'Government Medical College Thiruvananthapuram', state: 'Kerala', type: 'Government', course: 'MBBS', seats: 200, annualFees: 18000, openingRank: 1100, closingRank: 1700, probability: 'Moderate', nirf: null, city: 'Thiruvananthapuram' },
  { id: 'sms-jai', college: 'SMS Medical College Jaipur', state: 'Rajasthan', type: 'Government', course: 'MBBS', seats: 250, annualFees: 35000, openingRank: 1200, closingRank: 1800, probability: 'Moderate', nirf: null, city: 'Jaipur' },
  { id: 'bmcri-ban', college: 'Bangalore Medical College & Research Institute', state: 'Karnataka', type: 'Government', course: 'MBBS', seats: 250, annualFees: 55000, openingRank: 1600, closingRank: 2100, probability: 'Moderate', nirf: 12, city: 'Bangalore' },
  { id: 'mmc-che', college: 'Madras Medical College', state: 'Tamil Nadu', type: 'Government', course: 'MBBS', seats: 250, annualFees: 14500, openingRank: 1800, closingRank: 2400, probability: 'Moderate', nirf: 9, city: 'Chennai' },
  { id: 'bjmc-ahm', college: 'BJ Medical College Ahmedabad', state: 'Gujarat', type: 'Government', course: 'MBBS', seats: 200, annualFees: 48000, openingRank: 2800, closingRank: 3600, probability: 'Moderate', nirf: null, city: 'Ahmedabad' },
  { id: 'nrs-kol', college: 'NRS Medical College Kolkata', state: 'West Bengal', type: 'Government', course: 'MBBS', seats: 200, annualFees: 25000, openingRank: 1500, closingRank: 2200, probability: 'Moderate', nirf: null, city: 'Kolkata' },
  { id: 'osmc-hyd', college: 'Osmania Medical College', state: 'Telangana', type: 'Government', course: 'MBBS', seats: 250, annualFees: 28000, openingRank: 1400, closingRank: 2100, probability: 'Moderate', nirf: null, city: 'Hyderabad' },
  { id: 'gmc-bho', college: 'Gandhi Medical College Bhopal', state: 'Madhya Pradesh', type: 'Government', course: 'MBBS', seats: 200, annualFees: 32000, openingRank: 1600, closingRank: 2400, probability: 'Moderate', nirf: null, city: 'Bhopal' },
  { id: 'jnmc-bel', college: 'Jawaharlal Nehru Medical College Belagavi', state: 'Karnataka', type: 'Deemed', course: 'MBBS', seats: 150, annualFees: 1250000, openingRank: 3500, closingRank: 7800, probability: 'Moderate', nirf: 22, city: 'Belagavi' },
  { id: 'kmc-man', college: 'Kasturba Medical College Manipal', state: 'Karnataka', type: 'Deemed', course: 'MBBS', seats: 250, annualFees: 1900000, openingRank: 5000, closingRank: 11000, probability: 'Low', nirf: 14, city: 'Manipal' },
  { id: 'amr-koc', college: 'Amrita Institute of Medical Sciences', state: 'Kerala', type: 'Deemed', course: 'MBBS', seats: 150, annualFees: 1700000, openingRank: 4500, closingRank: 8500, probability: 'Low', nirf: 20, city: 'Kochi' },
  { id: 'kims-hyd', college: 'Krishna Institute of Medical Sciences', state: 'Telangana', type: 'Private', course: 'MBBS', seats: 150, annualFees: 750000, openingRank: 5000, closingRank: 10000, probability: 'Low', nirf: null, city: 'Hyderabad' },
  { id: 'ms-ram-ban', college: 'MS Ramaiah Medical College', state: 'Karnataka', type: 'Private', course: 'MBBS', seats: 150, annualFees: 650000, openingRank: 4000, closingRank: 8000, probability: 'Low', nirf: null, city: 'Bangalore' },
  { id: 'maulana-bds', college: 'Maulana Azad Institute of Dental Sciences', state: 'Delhi', type: 'Government', course: 'BDS', seats: 100, annualFees: 22000, openingRank: 500, closingRank: 900, probability: 'High', nirf: null, city: 'New Delhi' },
  { id: 'gdc-ban', college: 'Government Dental College Bangalore', state: 'Karnataka', type: 'Government', course: 'BDS', seats: 100, annualFees: 18000, openingRank: 1000, closingRank: 2000, probability: 'High', nirf: null, city: 'Bangalore' },
  { id: 'bhu-bams', college: 'BHU Faculty of Ayurveda', state: 'Uttar Pradesh', type: 'Government', course: 'BAMS', seats: 100, annualFees: 15000, openingRank: 10000, closingRank: 25000, probability: 'Moderate', nirf: null, city: 'Varanasi' },
  { id: 'gac-jaipur', college: 'Government Ayurvedic College Jaipur', state: 'Rajasthan', type: 'Government', course: 'BAMS', seats: 100, annualFees: 12000, openingRank: 20000, closingRank: 40000, probability: 'Low', nirf: null, city: 'Jaipur' },
  { id: 'nhmc-del', college: 'National Homeopathy Medical College Delhi', state: 'Delhi', type: 'Government', course: 'BHMS', seats: 100, annualFees: 10000, openingRank: 30000, closingRank: 60000, probability: 'Low', nirf: null, city: 'New Delhi' },
];

const CATEGORY_MULTIPLIERS: Record<string, number> = {
  General: 1.0, EWS: 1.0, OBC: 1.35, SC: 2.2, ST: 3.5, PwD: 8.0,
};

function getEffectiveRank(rank: number, category: string, quota: string): number {
  const catMult = CATEGORY_MULTIPLIERS[category] || 1.0;
  const quotaMult = quota === 'State' ? 2.5 : 1.0;
  return Math.round(rank * catMult * quotaMult);
}

function computeProbability(userRank: number, closingRank: number): 'High' | 'Moderate' | 'Low' {
  if (userRank <= closingRank) return 'High';
  if (userRank <= closingRank * 1.15) return 'Moderate';
  return 'Low';
}

function predictColleges(form: PredictorForm): CollegeResult[] {
  const rank = parseInt(form.rank);
  if (!rank || rank <= 0) return [];
  const effectiveRank = getEffectiveRank(rank, form.category, form.quota);
  const maxRank = effectiveRank * 1.5;

  return COLLEGE_DATABASE
    .filter(c => {
      if (c.course !== form.course) return false;
      if (form.quota === 'State' && form.state !== 'All India' && c.state !== form.state) return false;
      if (form.collegeType && form.collegeType !== 'Any' && c.type !== form.collegeType) return false;
      if (c.annualFees > form.budgetMax && form.budgetMax > 0) return false;
      return c.closingRank >= effectiveRank * 0.5 && effectiveRank <= maxRank;
    })
    .map(c => ({ ...c, probability: computeProbability(effectiveRank, c.closingRank) }))
    .sort((a, b) => a.closingRank - b.closingRank);
}

const formatFees = (fees: number) => {
  if (fees >= 100000) return `₹${(fees / 100000).toFixed(1)}L/yr`;
  return `₹${fees.toLocaleString('en-IN')}/yr`;
};

const ProbBadge = ({ prob }: { prob: string }) => {
  if (prob === 'High') return <span className="prob-high flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"><TrendingUp size={10} /> High</span>;
  if (prob === 'Moderate') return <span className="prob-moderate flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"><Minus size={10} /> Moderate</span>;
  return <span className="prob-low flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"><TrendingDown size={10} /> Low</span>;
};

const TypeBadge = ({ type }: { type: string }) => {
  const cls = type === 'Government' || type === 'Central' ? 'badge-govt' : type === 'Private' ? 'badge-private' : 'badge-deemed';
  return <span className={`${cls} px-2 py-0.5 rounded-md text-xs font-semibold`}>{type}</span>;
};

// ============================================================
// LOADING SCREEN
// ============================================================
const LoadingScreen = () => {
  const [step, setStep] = useState(0);
  const steps = [
    'Analyzing your NEET rank...',
    'Matching colleges to your profile...',
    'Calculating admission probabilities...',
    'Preparing your personalized list...',
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setStep(prev => (prev < steps.length - 1 ? prev + 1 : prev));
    }, 600);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 flex flex-col items-center justify-center min-h-[60vh]">
      <div className="glass-card-white rounded-3xl p-10 border border-white/60 w-full text-center shadow-2xl">
        {/* Animated spinner */}
        <div className="relative w-24 h-24 mx-auto mb-8">
          <div className="absolute inset-0 rounded-full border-4 border-primary/20"></div>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary animate-spin"></div>
          <div className="absolute inset-2 rounded-full border-4 border-transparent border-t-violet-400 animate-spin" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Sparkles size={28} className="text-primary animate-pulse" />
          </div>
        </div>

        <h3 className="text-2xl font-extrabold text-foreground mb-2">Finding Your Best Colleges</h3>
        <p className="text-muted-foreground text-sm mb-8">Our AI is crunching the numbers for you</p>

        {/* Step indicators */}
        <div className="space-y-3 text-left max-w-xs mx-auto">
          {steps.map((s, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 transition-all duration-500 ${i <= step ? 'opacity-100' : 'opacity-25'}`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${i < step ? 'bg-primary' : i === step ? 'bg-primary animate-pulse' : 'bg-slate-200'}`}>
                {i < step ? (
                  <CheckCircle2 size={12} className="text-white" />
                ) : (
                  <div className={`w-2 h-2 rounded-full ${i === step ? 'bg-white' : 'bg-slate-400'}`} />
                )}
              </div>
              <span className={`text-sm font-medium ${i <= step ? 'text-foreground' : 'text-muted-foreground'}`}>{s}</span>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="mt-8 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary to-violet-400 rounded-full transition-all duration-700 ease-out"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// ============================================================
// ANIMATED COLLEGE ROW
// ============================================================
const AnimatedRow = ({ r, index, savedColleges, setSavedColleges }: {
  r: CollegeResult;
  index: number;
  savedColleges: string[];
  setSavedColleges: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), index * 80);
    return () => clearTimeout(timer);
  }, [index]);

  return (
    <tr
      className="transition-all duration-500 ease-out"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
      }}
    >
      <td>
        <div>
          <p className="font-semibold text-foreground text-sm">{r.college}</p>
          {r.nirf && <p className="text-xs text-muted-foreground">NIRF #{r.nirf}</p>}
        </div>
      </td>
      <td className="text-sm text-muted-foreground">{r.state}</td>
      <td><TypeBadge type={r.type} /></td>
      <td className="text-sm font-semibold text-foreground">{formatFees(r.annualFees)}</td>
      <td className="text-sm text-muted-foreground">{r.seats}</td>
      <td className="text-sm text-muted-foreground">{r.openingRank.toLocaleString('en-IN')}</td>
      <td className="text-sm font-semibold text-foreground">{r.closingRank.toLocaleString('en-IN')}</td>
      <td><ProbBadge prob={r.probability} /></td>
      <td>
        <button
          onClick={() => {
            setSavedColleges(prev => prev.includes(r.id) ? prev.filter(x => x !== r.id) : [...prev, r.id]);
            toast.success(savedColleges.includes(r.id) ? 'Removed from saved' : 'College saved!');
          }}
          className={`p-1.5 rounded-lg transition-colors ${savedColleges.includes(r.id) ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'}`}
        >
          <BookmarkPlus size={15} />
        </button>
      </td>
    </tr>
  );
};

// ============================================================
// STEPS CONFIG
// ============================================================
const STEPS = [
  { id: 1, label: 'NEET Rank', field: 'rank' },
  { id: 2, label: 'Category', field: 'category' },
  { id: 3, label: 'State', field: 'state' },
  { id: 4, label: 'Quota', field: 'quota' },
  { id: 5, label: 'Course', field: 'course' },
  { id: 6, label: 'College Type', field: 'collegeType' },
];

const STATES = ['All India', 'Andhra Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Odisha', 'Punjab', 'Rajasthan', 'Tamil Nadu', 'Telangana', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'];

const BUDGET_OPTIONS = [
  { label: 'Under ₹1 Lakh/yr', min: 0, max: 100000 },
  { label: '₹1L – ₹5L/yr', min: 100000, max: 500000 },
  { label: '₹5L – ₹10L/yr', min: 500000, max: 1000000 },
  { label: '₹10L – ₹20L/yr', min: 1000000, max: 2000000 },
  { label: 'No Limit', min: 0, max: 99999999 },
];

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function NEETPredictorClient() {
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState<PredictorForm>({
    rank: '',
    category: 'General',
    state: 'All India',
    quota: 'AIQ',
    course: 'MBBS',
    budgetMin: 0,
    budgetMax: 99999999,
    collegeType: 'Any',
  });
  const [leadForm, setLeadForm] = useState<LeadForm>({ name: '', mobile: '', email: '' });
  const [leadErrors, setLeadErrors] = useState<Record<string, string>>({});
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<CollegeResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [sortBy, setSortBy] = useState<'closingRank' | 'fees' | 'nirf'>('closingRank');
  const [filterType, setFilterType] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [savedColleges, setSavedColleges] = useState<string[]>([]);

  const progress = (currentStep / STEPS.length) * 100;

  const canProceed = () => {
    if (currentStep === 1) return form.rank && parseInt(form.rank) > 0;
    return true;
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) setCurrentStep(s => s + 1);
    else setShowLeadModal(true);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(s => s - 1);
  };

  const validateLead = () => {
    const errs: Record<string, string> = {};
    if (!leadForm.name.trim()) errs.name = 'Full name is required';
    if (!leadForm.mobile.match(/^[6-9]\d{9}$/)) errs.mobile = 'Enter valid 10-digit mobile';
    if (!leadForm.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) errs.email = 'Enter valid email';
    setLeadErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submitLead = async () => {
    if (!validateLead()) return;
    setLoading(true);
    setShowLeadModal(false);

    try {
      const supabase = createClient();
      await supabase.from('leads').insert({
        name: leadForm.name,
        email: leadForm.email,
        mobile: leadForm.mobile,
        neet_rank: parseInt(form.rank),
        category: form.category,
        state: form.state,
        quota: form.quota,
        course: form.course,
        budget_min: form.budgetMin,
        budget_max: form.budgetMax,
        college_type_preference: form.collegeType,
        lead_status: 'new',
      });
    } catch {
      // Continue even if Supabase fails
    }

    // Save to Google Sheets
    try {
      await fetch('/api/save-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: leadForm.name,
          mobile: leadForm.mobile,
          email: leadForm.email,
          neetRank: form.rank,
          category: form.category,
          state: form.state,
          quota: form.quota,
          course: form.course,
          collegeType: form.collegeType,
        }),
      });
    } catch {
      // Continue even if Google Sheets fails
    }

    const predicted = predictColleges(form);
    setResults(predicted);
    setLoading(false);
    setShowResults(true);
    toast.success(`Found ${predicted.length} matching colleges for your profile!`);
  };

  const filteredResults = results
    .filter(r => filterType.length === 0 || filterType.includes(r.type))
    .filter(r => !searchQuery || r.college.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'closingRank') return a.closingRank - b.closingRank;
      if (sortBy === 'fees') return a.annualFees - b.annualFees;
      if (sortBy === 'nirf') return (a.nirf ?? 999) - (b.nirf ?? 999);
      return 0;
    });

  const highCount = results.filter(r => r.probability === 'High').length;
  const moderateCount = results.filter(r => r.probability === 'Moderate').length;
  const admissionScore = results.length > 0 ? Math.min(99, Math.round((highCount / results.length) * 100 + 20)) : 0;

  if (loading) {
    return <LoadingScreen />;
  }

  if (showResults) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Results Header */}
        <div className="glass-card-white rounded-3xl p-6 mb-6 border border-white/60">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-extrabold text-foreground mb-1">Your Prediction Results</h2>
              <p className="text-muted-foreground text-sm">
                NEET Rank <strong>{form.rank}</strong> · {form.category} · {form.quota} Quota · {form.course}
              </p>
            </div>
            <button
              onClick={() => { setShowResults(false); setCurrentStep(1); setResults([]); }}
              className="btn-secondary text-sm px-4 py-2 rounded-xl flex items-center gap-2"
            >
              <ArrowRight size={14} className="rotate-180" />
              New Prediction
            </button>
          </div>

          {/* Score cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            <div className="glass-card rounded-2xl p-4 text-center">
              <div className="relative w-16 h-16 mx-auto mb-2">
                <svg className="w-16 h-16 score-ring" viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#e2e8f0" strokeWidth="6" />
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#7c3aed" strokeWidth="6"
                    strokeDasharray={`${(admissionScore / 100) * 175.9} 175.9`}
                    strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-extrabold text-primary">{admissionScore}</span>
              </div>
              <p className="text-xs font-semibold text-muted-foreground">Admission Score</p>
            </div>
            <div className="glass-card rounded-2xl p-4 text-center">
              <p className="text-3xl font-extrabold text-foreground">{results.length}</p>
              <p className="text-xs font-semibold text-muted-foreground mt-1">Matching Colleges</p>
            </div>
            <div className="glass-card rounded-2xl p-4 text-center">
              <p className="text-3xl font-extrabold text-success">{highCount}</p>
              <p className="text-xs font-semibold text-muted-foreground mt-1">High Probability</p>
            </div>
            <div className="glass-card rounded-2xl p-4 text-center">
              <p className="text-3xl font-extrabold text-warning">{moderateCount}</p>
              <p className="text-xs font-semibold text-muted-foreground mt-1">Moderate Chance</p>
            </div>
          </div>
        </div>

        {/* Filters & Sort */}
        <div className="glass-card-white rounded-2xl p-4 mb-5 border border-white/60">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search colleges..."
                className="input-glass pl-9 text-sm"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {['Government', 'Private', 'Deemed'].map(t => (
                <button
                  key={`ft-${t}`}
                  onClick={() => setFilterType(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])}
                  className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${filterType.includes(t) ? 'bg-primary text-white border-primary' : 'bg-white border-slate-200 text-muted-foreground hover:border-primary/30'}`}
                >
                  {t}
                </button>
              ))}
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value as typeof sortBy)}
                className="select-glass text-xs py-2 px-3 w-auto"
              >
                <option value="closingRank">Best Match</option>
                <option value="fees">Lowest Fees</option>
                <option value="nirf">Highest Ranked</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Table */}
        <div className="glass-card-white rounded-2xl border border-white/60 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>College</th>
                  <th>State</th>
                  <th>Type</th>
                  <th>Fees/yr</th>
                  <th>Seats</th>
                  <th>Opening Rank</th>
                  <th>Closing Rank</th>
                  <th>Probability</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((r, index) => (
                  <AnimatedRow
                    key={r.id}
                    r={r}
                    index={index}
                    savedColleges={savedColleges}
                    setSavedColleges={setSavedColleges}
                  />
                ))}
              </tbody>
            </table>
            {filteredResults.length === 0 && (
              <div className="text-center py-16 text-muted-foreground">
                <Brain size={40} className="mx-auto mb-3 opacity-30" />
                <p className="font-semibold">No colleges match your filters</p>
                <p className="text-sm mt-1">Try adjusting your filters or budget range</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      {/* Lead Modal */}
      {showLeadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
          <div className="glass-card-white rounded-3xl p-8 w-full max-w-md shadow-2xl border border-white/60 animate-slide-up">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-extrabold text-foreground">Almost There!</h3>
                <p className="text-sm text-muted-foreground mt-1">Enter your details to see your personalized college list</p>
              </div>
              <button onClick={() => setShowLeadModal(false)} className="p-2 rounded-xl hover:bg-slate-100 text-muted-foreground">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Full Name *</label>
                <input type="text" value={leadForm.name} onChange={e => setLeadForm(p => ({ ...p, name: e.target.value }))} placeholder=" " className="input-glass" />
                {leadErrors.name && <p className="text-xs text-danger mt-1">{leadErrors.name}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Mobile Number *</label>
                <input type="tel" value={leadForm.mobile} onChange={e => setLeadForm(p => ({ ...p, mobile: e.target.value }))} placeholder=" " className="input-glass" />
                {leadErrors.mobile && <p className="text-xs text-danger mt-1">{leadErrors.mobile}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Email Address *</label>
                <input type="email" value={leadForm.email} onChange={e => setLeadForm(p => ({ ...p, email: e.target.value }))} placeholder=" " className="input-glass" />
                {leadErrors.email && <p className="text-xs text-danger mt-1">{leadErrors.email}</p>}
              </div>
              <button onClick={submitLead} disabled={loading} className="btn-primary w-full py-3.5 justify-center text-base gap-2">
                {loading ? <LoaderCircle size={18} className="animate-spin" /> : <Sparkles size={18} />}
                Generate My College List
              </button>
              <p className="text-xs text-center text-muted-foreground">Your data is secure. We never spam.</p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="glass-card-white rounded-2xl p-5 mb-6 border border-white/60">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-foreground">Step {currentStep} of {STEPS.length}</p>
          <p className="text-sm text-muted-foreground">{STEPS[currentStep - 1]?.label}</p>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex items-center gap-1 mt-3 overflow-x-auto pb-1">
          {STEPS.map(step => (
            <div key={step.id} className="flex items-center gap-1 flex-shrink-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                step.id < currentStep ? 'step-completed' : step.id === currentStep ? 'step-active' : 'step-inactive'
              }`}>
                {step.id < currentStep ? <CheckCircle2 size={14} /> : step.id}
              </div>
              {step.id < STEPS.length && <div className={`w-6 h-0.5 rounded-full ${step.id < currentStep ? 'bg-success' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="glass-card-white rounded-3xl p-8 border border-white/60 shadow-lg animate-fade-in">
        {currentStep === 1 && (
          <div>
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
              <Sparkles size={26} className="text-primary" />
            </div>
            <h2 className="text-2xl font-extrabold text-foreground mb-2">What is your NEET Rank?</h2>
            <p className="text-muted-foreground text-sm mb-6">Enter your All India Rank (AIR) from NEET 2026 result</p>
            <input
              type="number"
              value={form.rank}
              onChange={e => setForm(p => ({ ...p, rank: e.target.value }))}
              placeholder="e.g. 8500"
              className="input-glass text-2xl font-bold py-4 text-center"
              autoFocus
            />
            <p className="text-xs text-muted-foreground mt-3 text-center">Enter rank between 1 – 20,00,000</p>
          </div>
        )}

        {currentStep === 2 && (
          <div>
            <h2 className="text-2xl font-extrabold text-foreground mb-2">Select Your Category</h2>
            <p className="text-muted-foreground text-sm mb-6">Category determines your effective rank for admission</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {['General', 'OBC', 'SC', 'ST', 'EWS', 'PwD'].map(cat => (
                <button
                  key={cat}
                  onClick={() => setForm(p => ({ ...p, category: cat }))}
                  className={`p-4 rounded-2xl border-2 text-sm font-bold transition-all ${form.category === cat ? 'border-primary bg-primary/8 text-primary' : 'border-slate-200 text-muted-foreground hover:border-primary/30 hover:text-foreground'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div>
            <h2 className="text-2xl font-extrabold text-foreground mb-2">Select Your State</h2>
            <p className="text-muted-foreground text-sm mb-6">Used for state quota eligibility calculation</p>
            <div className="relative">
              <select value={form.state} onChange={e => setForm(p => ({ ...p, state: e.target.value }))} className="select-glass text-base py-4">
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div>
            <h2 className="text-2xl font-extrabold text-foreground mb-2">Select Quota</h2>
            <p className="text-muted-foreground text-sm mb-6">Choose the admission quota you want to apply under</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'AIQ', label: 'All India Quota', desc: '15% seats, national competition' },
                { value: 'State', label: 'State Quota', desc: '85% seats, state domicile' },
                { value: 'Management', label: 'Management Quota', desc: 'Private/Deemed colleges' },
                { value: 'NRI', label: 'NRI Quota', desc: 'For NRI/OCI candidates' },
              ].map(q => (
                <button
                  key={q.value}
                  onClick={() => setForm(p => ({ ...p, quota: q.value }))}
                  className={`p-4 rounded-2xl border-2 text-left transition-all ${form.quota === q.value ? 'border-primary bg-primary/8' : 'border-slate-200 hover:border-primary/30'}`}
                >
                  <p className={`text-sm font-bold ${form.quota === q.value ? 'text-primary' : 'text-foreground'}`}>{q.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">{q.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {currentStep === 5 && (
          <div>
            <h2 className="text-2xl font-extrabold text-foreground mb-2">Select Course</h2>
            <p className="text-muted-foreground text-sm mb-6">Choose your preferred medical course</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {['MBBS', 'BDS', 'BAMS', 'BHMS', 'BUMS', 'BSMS'].map(course => (
                <button
                  key={course}
                  onClick={() => setForm(p => ({ ...p, course }))}
                  className={`p-4 rounded-2xl border-2 text-sm font-bold transition-all ${form.course === course ? 'border-primary bg-primary/8 text-primary' : 'border-slate-200 text-muted-foreground hover:border-primary/30 hover:text-foreground'}`}
                >
                  {course}
                </button>
              ))}
            </div>
          </div>
        )}

        {currentStep === 6 && (
          <div>
            <h2 className="text-2xl font-extrabold text-foreground mb-2">College Type Preference</h2>
            <p className="text-muted-foreground text-sm mb-6">Filter results by college type</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'Any', label: 'All Types', desc: 'Show all college types' },
                { value: 'Government', label: 'Government Only', desc: 'Lowest fees, highest competition' },
                { value: 'Private', label: 'Private Only', desc: 'More seats, higher fees' },
                { value: 'Deemed', label: 'Deemed Only', desc: 'Autonomous universities' },
              ].map(t => (
                <button
                  key={t.value}
                  onClick={() => setForm(p => ({ ...p, collegeType: t.value }))}
                  className={`p-4 rounded-2xl border-2 text-left transition-all ${form.collegeType === t.value ? 'border-primary bg-primary/8' : 'border-slate-200 hover:border-primary/30'}`}
                >
                  <p className={`text-sm font-bold ${form.collegeType === t.value ? 'text-primary' : 'text-foreground'}`}>{t.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className="btn-secondary flex items-center gap-2 px-5 py-3 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={16} />
            Back
          </button>
          <button
            onClick={handleNext}
            disabled={!canProceed()}
            className="btn-primary flex items-center gap-2 px-6 py-3 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {currentStep === STEPS.length ? (
              <>
                <Sparkles size={16} />
                Generate Prediction
              </>
            ) : (
              <>
                Next
                <ChevronRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}